/// <amd-module name="VSSUI/Components/SizeObserver/SizeObserver" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { ISizeObserverProps } from "./SizeObserver.Props";

/**
 * Measures all the size of a wrapper around all child components
 * Be aware: this component creates additional hidden DOM elements. Use carefully as it may have unintended effects on your layout.
 * Use with caution; improper use can cause severe layout thrashing
 */
export class SizeObserver extends React.Component<ISizeObserverProps> {
    private static readonly intersectionThresholds = [0, 0.25, 0.5, 0.75, 1];

    private intersectionObserver: IntersectionObserver;

    private horizontalIntersectorRef = React.createRef<HTMLDivElement>();
    private horizontalIntersectorStyle = new ObservableValue<React.CSSProperties>({});
    private verticalIntersectorRef = React.createRef<HTMLDivElement>();
    private verticalIntersectorStyle = new ObservableValue<React.CSSProperties>({});

    private rootRef = React.createRef<HTMLDivElement>();

    private lastWidth: number = -1;
    private lastHeight: number = -1;

    public render() {
        return (
            <div className="bolt-size-observer-parent relative" ref={this.rootRef}>
                <div className="bolt-size-observer-container">
                    <Observer key="vertical-intersector" intersectorStyle={this.verticalIntersectorStyle}>
                        {(observerProps: { intersectorStyle: React.CSSProperties }) => (
                            <div
                                className="bolt-size-observer-observe vertical absolute invisible"
                                ref={this.verticalIntersectorRef}
                                style={observerProps.intersectorStyle}
                            />
                        )}
                    </Observer>

                    <Observer key="horizontal-intersector" intersectorStyle={this.horizontalIntersectorStyle}>
                        {(observerProps: { intersectorStyle: React.CSSProperties }) => (
                            <div
                                className="bolt-measure-observe horizontal absolute invisible"
                                ref={this.horizontalIntersectorRef}
                                style={observerProps.intersectorStyle}
                            />
                        )}
                    </Observer>
                </div>
                {this.props.children}
            </div>
        );
    }

    public componentDidMount() {
        const intersectionOptions: IntersectionObserverInit = {
            root: this.rootRef.current!,
            threshold: SizeObserver.intersectionThresholds
        };

        this.intersectionObserver = new IntersectionObserver(this.onResize, intersectionOptions);
        this.intersectionObserver.observe(this.verticalIntersectorRef.current!);
        this.intersectionObserver.observe(this.horizontalIntersectorRef.current!);

        window.addEventListener("resize", this.onResize);
    }

    public componentWillUnmount() {
        this.intersectionObserver.disconnect();
        window.removeEventListener("resize", this.onResize);
    }

    private onResize = () => {
        const rootElement = this.rootRef.current!;
        const newRootWidth = rootElement.clientWidth;
        const newRootHeight = rootElement.clientHeight;

        // Anti-layout-thrashing - don't pass back up if size hasn't actually changed
        if (this.lastWidth != newRootWidth || this.lastHeight != newRootHeight) {
            this.props.onSizeChange && this.props.onSizeChange(newRootWidth, newRootHeight);

            // Update observer positions to
            this.horizontalIntersectorStyle.value = {
                left: `${newRootWidth - 1}px`
            };

            this.verticalIntersectorStyle.value = {
                top: `${newRootHeight - 1}px`
            };

            this.lastWidth = newRootWidth;
            this.lastHeight = newRootHeight;
        }
    };
}
